/**
 * A task can't be checked off — completed on a normal list, or marked "done" on
 * a review list — while it still has open sub-tasks or unchecked checklist
 * items. Same precondition the review flow's "Send for review" gate uses, now
 * applied at the checkbox so it can't even be marked done early. Driven entirely
 * by the server-computed counts already on each item, so it needs no extra data
 * (a sub-task assigned to someone else still counts).
 *
 * Note: the DB supports personal sub-tasks (`parent_personal_item_id`), but the
 * UI never exposes a "+S" on private tasks, so personal sub-tasks aren't
 * creatable in practice — for personal items only the checklist counts matter.
 */
export interface CompletionBlockers {
  openSubtasks: number;
  openChecklist: number;
  /** True when the task has any open sub-item and so may not be completed. */
  blocked: boolean;
}

export function getCompletionBlockers(counts: {
  subtaskTotal?: number | null;
  subtaskDone?: number | null;
  checklistTotal?: number | null;
  checklistDone?: number | null;
}): CompletionBlockers {
  const openSubtasks = Math.max(0, (counts.subtaskTotal ?? 0) - (counts.subtaskDone ?? 0));
  const openChecklist = Math.max(0, (counts.checklistTotal ?? 0) - (counts.checklistDone ?? 0));
  return { openSubtasks, openChecklist, blocked: openSubtasks > 0 || openChecklist > 0 };
}

/** Human-readable reason for the "can't check this off yet" toast. */
export function completionBlockedMessage(b: CompletionBlockers): string {
  const parts: string[] = [];
  if (b.openSubtasks > 0)
    parts.push(`${b.openSubtasks} open sub-task${b.openSubtasks === 1 ? '' : 's'}`);
  if (b.openChecklist > 0)
    parts.push(`${b.openChecklist} unchecked checklist item${b.openChecklist === 1 ? '' : 's'}`);
  return `This task still has ${parts.join(' and ')}.`;
}
